import { useCallback, useEffect, useMemo, useRef } from "react";

import { fetchTours } from "@/libs/api/requests";
import { createToursList } from "../utils";
import { useTourContext } from "./useTourContext";
import { getLogger } from "@/utils/logger";

let isLoadingTours = false;

const log = getLogger("app:tours:hooks:useTours");

export function useTours(driverId: string | undefined) {
  const { tours, loading, error, selectedDate, setContext } = useTourContext();

  const lastFetchedDate = useRef<Date | undefined>(undefined);

  const handleLoadTours = useCallback(
    async (date: Date | undefined = new Date()) => {
      if (
        isLoadingTours ||
        lastFetchedDate.current?.getTime() === date.getTime() ||
        !driverId
      ) {
        return;
      }

      isLoadingTours = true;
      lastFetchedDate.current = date;

      setContext({
        type: "new",
        newState: {
          loading: true,
          error: null,
          tours: [],
          selectedDate: date,
        },
      });

      // TODO: implement loading action and error handling
      fetchTours(date, driverId)
        .then((response) => {
          if (!response || !response.items) {
            setContext({
              type: "new",
              newState: {
                selectedDate: date,
                tours: [],
                loading: false,
                error: null,
              },
            });
            return;
          }

          const _tours = createToursList(response);

          setContext({
            type: "new",
            newState: {
              selectedDate: date,
              tours: _tours,
              loading: false,
              error: null,
            },
          });

          log("Successfully fetched tours");
        })
        .catch((err) => {
          setContext({
            type: "error",
            error: err.message || "Failed to load tours",
          });
          log("Failed to fetch tours", err);
        })
        .finally(() => {
          isLoadingTours = false;
        });
    },
    [driverId, setContext],
  );

  useEffect(() => {
    handleLoadTours(selectedDate);
  }, [handleLoadTours, selectedDate]);

  return useMemo(
    () => ({
      tours,
      loading,
      error,
      selectedDate,
      handleLoadTours,
    }),
    [tours, loading, error, selectedDate, handleLoadTours],
  );
}
